import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';
import { ArrowRight, CornerDownLeft, Sparkles } from 'lucide-react';
import { askHelp } from '../../../pages/help/helpCenterApi';
import { AnswerText } from '../../../pages/help/HelpAsk';
import './paletteHelpAnswer.css';

const MIN_CHARS = 3;
const DEBOUNCE_MS = 700;

/*
 * ⌘K with nothing to pick: instead of "No results found", the typed text goes
 * to the Help Center agent (azfApi/helpCenter/ask.js) once typing pauses, and
 * the answer shows in the palette. Enter continues in the Help Center with
 * follow-ups. Rendered inside CommandEmpty, so it only exists while no page,
 * report or version matches.
 */
export default function PaletteHelpAnswer({ query, onOpenHelp, onLeave }) {
  const posthog = usePostHog();
  const { pathname, search } = useLocation();
  const q = query.trim();
  const active = q.length >= MIN_CHARS;
  const [state, setState] = useState({ status: 'idle' });

  useEffect(() => {
    if (!active) {
      setState({ status: 'idle' });
      return undefined;
    }
    let cancelled = false;
    setState({ status: 'waiting' });
    const timer = setTimeout(async () => {
      setState({ status: 'loading' });
      try {
        // The page behind the palette, with its filters, so answers link back into it.
        const res = await askHelp({ question: q, route: pathname + search });
        if (cancelled) return;
        setState({ status: 'done', answer: res.answer, suggestion: res.suggestion });
        posthog?.capture?.('help_palette_ask', { suggested: res.suggestion?.slug || null });
      } catch (err) {
        if (!cancelled) setState({ status: 'error', message: err?.response?.data?.error || 'The Help Center couldn’t answer right now.' });
      }
    }, DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [active, q]); // eslint-disable-line react-hooks/exhaustive-deps

  // An answer already on screen goes along, so the Help Center doesn't ask twice.
  const seedRef = useRef(null);
  seedRef.current = state.status === 'done' ? { q, answer: state.answer, suggestion: state.suggestion } : null;
  const openHelp = () => onOpenHelp(q, seedRef.current);

  // Nothing to select, so Enter means "ask": continue in the Help Center.
  useEffect(() => {
    if (!active) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Enter' || e.isComposing) return;
      e.preventDefault();
      onOpenHelp(q, seedRef.current);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [active, q, onOpenHelp]);

  if (!active) return <p className="palette-help-none">No results found.</p>;

  return (
    <div className="palette-help" aria-live="polite">
      <button type="button" className="palette-help-ask" onClick={openHelp}>
        <Sparkles aria-hidden="true" />
        <span className="palette-help-q">Ask the Help Center: <strong>{q}</strong></span>
        <span className="palette-help-kbd">Open <CornerDownLeft aria-hidden="true" /></span>
      </button>

      {state.status === 'waiting' || state.status === 'loading' ? (
        <div className="palette-help-loading" aria-label="Finding the answer">
          <span className="lodge-skeleton" style={{ width: '86%' }} />
          <span className="lodge-skeleton" style={{ width: '64%' }} />
          <span className="lodge-skeleton" style={{ width: '72%' }} />
        </div>
      ) : null}

      {state.status === 'error' ? <p className="palette-help-error">{state.message}</p> : null}

      {state.status === 'done' ? (
        // Following a link in the answer leaves the palette.
        <div className="palette-help-answer" onClickCapture={(e) => { if (e.target.closest('a')) onLeave(); }}>
          <AnswerText text={state.answer} />
          {state.suggestion?.url ? (
            <Link className="palette-help-guide" to={state.suggestion.url}>
              <span className="palette-help-guide-label">Suggested guide</span>
              <span>{state.suggestion.title}</span>
              <ArrowRight aria-hidden="true" />
            </Link>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
